import merge from 'lodash/merge';
import cloneDeep from 'lodash/fp/cloneDeep';
import businessService from '../services/businessService';
import helperFunctions from '../utils/helperFunctions';
import { bankList } from '../utils/credit';
import { AVRProducts, MarketplaceShortCodes, PrequalFinanceProviderCodes } from '../utils/enums';
import creditDataSourceCA from '@/utils/enums/creditDataSourceCA';

const experimentDefaults = {
  experiments: {
    tradePostalCode: 0,
  },
  featureList: {
    buttonWidgetProducts: null,
    tradePostalCodeRequired: false,
    tradeVehicleOfInterest: false,
    creditPostalCodeFirstPage: false,
    acquirePostalCodeRequired: false,
  },
};

const initialState = {
  supportedBusinesses: {
    InsuranceStrip: ['ECON', 'GA', 'HAL', 'WU'],
  },
  info: {
    products: [],
    name: '',
    address: {
      address_line_1: '',
      address_line_2: '',
      city: '',
      postal_code: '',
      province_id: '',
      country_code: '',
    },
    phone: {
      country_code: '',
      phone_number: '',
      extensions: '',
    },
    phoneNumberVerificationArray: {},
    tradeInSource: '',
    creditBureauPref: '',
    status: null,
    experiment: cloneDeep(experimentDefaults),
    gmbProfileId: null,
    banks: [],
    ecomV2: false,
    ecomV2Settings: {
      discountUpperThreshold: null,
      discountLowerThreshold: null,
      disclaimers: {
        used: {
          en: null,
          fr: null,
        },
        new: {
          en: null,
          fr: null,
        },
      },
    },
    creditSettings: {
      defaultCreditDataSourceCA: '',
      enableSSNCapture: false,
      requireSSN: false,
      prequalificationEnabled: false,
      prequalificationFinanceProviders: [],
      prequalificationTradeInEquityAssessmentEnabled: false,
      prequalificationIncomeAndDebtEnabled: false,
      prequalificationConsumerInventoryMatchingEnabled: false,
      prequalificationEnhancedEquifaxTradeInEquityAssessmentEnabled: false,
      financeApplicationEnabled: false,
    },
    integrationSettings: {
      country: null,
      province: null,
      postalCode: null,
      showSiriusXMFooter: false,
      VOIVinDecodeDisabled: false,
      tradeAutosEnabled: false,
      tradeSREnabled: false,
      ASCAnalyticsEnabled: false,
      tradeAcquireAdditionalVehicleDetails: false,
    },
    businessUrl: '',
    buildAndPriceSettings: {
      disclaimers: {
        en: null,
        fr: null,
      },
    },
    marketplaceSettings: {
      isMarketplace: false,
      marketplaceShortCode: '',
      marketplaceName: '',
      isMarketplaceParent: false,
    },
    isDemo: false,
    analytics: {
      shiftDigital: {
        sdClientId: '',
        sdRetailerId: '',
      },
    },
  },
  siriusXmInfo: {},
  api: {
    key: '',
    externalID: '',
  },
  bankList,
  dealerships: [],
  selectedDealership: null,
};

const mutations = {
  SET_THIS_IS_DEMO_SITE(state, data) {
    if (typeof data === 'boolean') {
      state.info.isDemo = data;
    }
  },
  SET_INFO(state, data) {
    state.info = data;
  },
  SET_API_DATA(state, { key, externalId }) {
    state.api.key = key;
    state.api.externalID = externalId;
  },
  HIDE_PRODUCT(state, { products }) {
    if (products.length > 0) {
      products.forEach(product => {
        const hasProduct = state.info.products.indexOf(product);
        if (hasProduct >= 0) {
          state.info.products.splice(hasProduct, 1);
        }
      });
    }
  },
  SET_SIRIUS_XM_INFO(state, payload) {
    state.siriusXmInfo = payload;
  },
  SET_DEALERSHIPS(state, payload) {
    state.dealerships = payload;
  },
  SET_SELECTED_DEALERSHIP(state, payload) {
    state.selectedDealership = payload;
  },
};
const getters = {
  getSubscribedProducts: state => state.info.products,
  getSupported: state => state.supportedBusinesses,
  getBankStrips: state => state.info.banks,
  getBusinessName: state => state.info.name,
  getBusinessCountry: state => state.info.address.country_code,
  getIntegrationProvince: state => state.info.integrationSettings.province,
  getCreditBureauPref: state => state.info.creditBureauPref,
  getThisIsAmerica: state => state.info.address.country_code.toLowerCase() === 'us',
  getBankList: state => state.bankList,
  getTradeInSource: state => state.info.tradeInSource,
  getFeatureList: state => state.info.experiment.featureList,
  getPhoneNumberVerificationArray: state => state.info.phoneNumberVerificationArray,
  isActive: state => state.info.status === 'active',
  isEcomV2Enabled: state => state.info.ecomV2,
  getBusinessFullAddress: state =>
    `${state.info.address.address_line_1}${
      state.info.address.address_line_2 ? ` ${state.info.address.address_line_2}` : ''
    }, ${state.info.address.city} ${state.info.address.province_id}, ${state.info.address.postal_code}`,
  getBusinessPhone: state => state.info.phone,
  getBusinessUrl: state => state.info.businessUrl,
  // eslint-disable-next-line no-shadow
  showSiriusXMBanner: (state, getters, rootState) =>
    'showSiriusXMFooter' in state.info.integrationSettings &&
    state.info.integrationSettings.showSiriusXMFooter &&
    !(rootState.credit.CIMFinanceApplicationVehicle && helperFunctions.isAVFinanceFlow(rootState.route.path)),
  isSiriusXmInfoRetrieved: state => Object.values(state.siriusXmInfo).length > 0,
  isCreditSSNRequired: state => state.info.creditSettings.enableSSNCapture && state.info.creditSettings.requireSSN,
  marketplaceShortCode: state => state.info.marketplaceSettings.marketplaceShortCode,
  isMarketplaceParent: state => state.info.marketplaceSettings.isMarketplaceParent,
  isKijijiDealer: state => state.info.marketplaceSettings.marketplaceShortCode === MarketplaceShortCodes.KIJIJI,
  isPrequalificationEnabled: state => state.info.creditSettings.prequalificationEnabled,
  getDefaultCreditDataSourceCA: state => state.info.creditSettings.defaultCreditDataSourceCA,
  prequalificationFinanceProviders: state => state.info.creditSettings.prequalificationFinanceProviders,
  prequalificationTradeInEquityAssessmentEnabled: state =>
    state.info.creditSettings.prequalificationEnabled &&
    state.info.creditSettings.prequalificationTradeInEquityAssessmentEnabled,
  prequalificationIncomeAndDebtEnabled: state =>
    state.info.creditSettings.prequalificationEnabled && state.info.creditSettings.prequalificationIncomeAndDebtEnabled,
  prequalificationConsumerInventoryMatchingEnabled: state =>
    state.info.creditSettings.prequalificationEnabled &&
    state.info.creditSettings.prequalificationConsumerInventoryMatchingEnabled,
  prequalificationEnhancedEquifaxTradeInEquityAssessmentEnabled: state =>
    state.info.creditSettings.prequalificationEnhancedEquifaxTradeInEquityAssessmentEnabled,
  financeApplicationEnabled: state => state.info.creditSettings.financeApplicationEnabled,
  // eslint-disable-next-line no-shadow
  displaySelectVehicleTypePage: (state, getters, rootState) =>
    getters.isPrequalificationEnabled &&
    getters.prequalificationFinanceProviders.length > 1 &&
    !rootState.route.query.category,
  isTDPrequalEnabled: state =>
    state.info.creditSettings.prequalificationEnabled &&
    state.info.creditSettings.prequalificationFinanceProviders &&
    (state.info.creditSettings.prequalificationFinanceProviders.includes(PrequalFinanceProviderCodes.TD) ||
      state.info.creditSettings.prequalificationFinanceProviders.includes(PrequalFinanceProviderCodes.TD_RV) ||
      state.info.creditSettings.prequalificationFinanceProviders.includes(PrequalFinanceProviderCodes.TD_MARINE) ||
      state.info.creditSettings.prequalificationFinanceProviders.includes(PrequalFinanceProviderCodes.TD_POWERSPORTS)),
  displayEquifaxBranding: state =>
    state.info.creditSettings.defaultCreditDataSourceCA === creditDataSourceCA.EQUIFAX ||
    state.info.creditSettings.defaultCreditDataSourceCA === creditDataSourceCA.EQUIFAX_ID_AUTH_NEIGHBOURHOOD_CREDIT,
  // eslint-disable-next-line no-shadow
  mobileVerificationEnabled: (state, getters, rootState, rootGetters) =>
    (rootGetters.getCurrentProduct === AVRProducts.TRADE && getters.getPhoneNumberVerificationArray.trade) ||
    (rootGetters.getCurrentProduct === AVRProducts.ACQUIRE && getters.getPhoneNumberVerificationArray.acquire) ||
    (rootGetters.getCurrentProduct === AVRProducts.INSURANCE && getters.getPhoneNumberVerificationArray.insurance),
  tradeAcquireAdditionalVehicleDetails: state => state.info.integrationSettings.tradeAcquireAdditionalVehicleDetails,
  displayMobileOnboardingPage: state => state.info.experiment.featureList.june2025NewMobileOnboardingPage,
};

const actions = {
  async getInfo(
    {
      // eslint-disable-next-line no-shadow
      commit,
      state,
      // eslint-disable-next-line no-shadow
      getters,
      dispatch,
      rootGetters,
      rootState,
    },
    { route }
  ) {
    try {
      const {
        // eslint-disable-next-line camelcase
        key,
        // eslint-disable-next-line camelcase
        external_id,
      } = route.query;
      const { path, meta } = route;

      commit('SET_LOADING', true, { root: true });
      commit('SET_API_DATA', {
        key,
        externalId: external_id,
      });

      const res = await businessService.getInfo(state.api.key, state.api.externalID);
      const data = cloneDeep(res.data);

      const isEcomV2FlowPath = path.includes('/e-commerce-v2/');
      const { isSpotlightReportApp } = rootState;

      data.products = await dispatch('getActiveProductsArray', {
        products: data.products,
        isEcomV2: isEcomV2FlowPath,
        meta,
      });

      // If the experiment value is an empty array, set a default values
      if (Array.isArray(data.experiment)) {
        data.experiment = experimentDefaults;
      } else {
        data.experiment = merge({}, experimentDefaults, data.experiment);
      }

      commit('SET_INFO', data);

      const currentProduct = rootGetters.getCurrentProduct;

      // Make sure the integration is active
      // and the current product is enabled (ecomv2 works a bit differently, is not included in data.products)
      // Exclude acquire because it is also not included in data.products
      // Exclude spotlight report too
      if (
        !getters.isActive ||
        (currentProduct &&
          currentProduct !== AVRProducts.ECOMV2 &&
          currentProduct !== AVRProducts.ACQUIRE &&
          !isSpotlightReportApp &&
          !data.products.includes(currentProduct)) ||
        (currentProduct === AVRProducts.ECOMV2 && !getters.isEcomV2Enabled)
      ) {
        throw new Error('Product or business is not enabled');
      }
    } catch (error) {
      throw error;
    } finally {
      commit('SET_LOADING', false, { root: true });
    }
  },
  // eslint-disable-next-line no-unused-vars
  async getActiveProductsArray({ commit, rootGetters, rootState }, { products, isEcomV2 }) {
    let productsArr;
    const { vin } = rootState.route.query;
    if (!vin) {
      // If there is no VIN, remove the insurance product from the list
      // eslint-disable-next-line no-param-reassign
      products[AVRProducts.INSURANCE] = false;
    }
    if (isEcomV2) {
      productsArr = []; // Do not trigger other tools init API if this is ecomv2
    } else {
      const validProductsArray = Object.values(AVRProducts);

      // Just use reviews to mean gmb and review for simplicity
      // since the front end treats them mostly the same
      if (products.gmbReviews === true) {
        // eslint-disable-next-line no-param-reassign
        products.reviews = true;
        // eslint-disable-next-line no-param-reassign
        products.gmbReviews = false;
        commit('reviews/SET_GMB_REVIEW_IS_ENABLED', true, { root: true });
      }

      // Create products array based on active and valid products
      productsArr = Object.keys(products).filter(product => products[product] && validProductsArray.includes(product));

      // Set the first product based on current product
      // If there is no current product, set "reviews" as a default
      const productName = rootGetters.getCurrentProduct ?? AVRProducts.REVIEWS;
      const valueIndex = productsArr.indexOf(productName);
      // if found, put at 0 index
      if (valueIndex !== -1) {
        productsArr.splice(valueIndex, 1);
        productsArr.unshift(productName);
      }
    }
    return productsArr;
  },
  async getDealerships({ rootState, commit }, payload) {
    try {
      const res = await businessService.getChildIntegrations(
        rootState.business.api.key,
        rootState.business.api.externalID,
        payload.postalCode ? payload.postalCode : '',
        payload.product ? payload.product : ''
      );
      commit('SET_DEALERSHIPS', res.data);
      // uncomment the following lines to use mock data instead of the API
      // let data;
      // if (payload && payload.postalCode) {
      //   // eslint-disable-next-line global-require
      //   data = require('@/mock/childDealershipsByPostalCode.json');
      //   // Simulate a delay for the mock data
      //   await new Promise(resolve => setTimeout(resolve, 500));
      // } else {
      //   // eslint-disable-next-line global-require
      //   data = require('@/mock/childDealerships.json');
      //   // Simulate a delay for the mock data
      //   await new Promise(resolve => setTimeout(resolve, 500));
      // }
      // commit('SET_DEALERSHIPS', data);
    } catch (error) {
      throw error;
    }
  },
};
export default {
  namespaced: true,
  state: cloneDeep(initialState),
  mutations,
  actions,
  getters,
};
